import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Send, ChevronLeft, MessageSquare, Reply, Trash2 } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function CommentBody({ c, isReply }) {
  return (
    <div className="flex items-start gap-2 min-w-0">
      <div className="w-8 h-8 rounded-full bg-[var(--navy-deep)] text-[var(--gold-soft)] grid place-items-center text-xs font-bold shrink-0 uppercase">
        {(c.user_name || "?").slice(0, 1)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-sm">{c.user_name}</div>
          <div className="text-[11px] text-zinc-500">{timeAgo(c.created_at)}</div>
          {isReply ? (
            <span className="text-[10px] bg-[var(--muted-surface)] text-zinc-600 rounded-full px-2 py-0.5 font-semibold">
              Reply
            </span>
          ) : null}
        </div>
        <div className="mt-1 text-sm text-zinc-800 whitespace-pre-wrap break-words">{c.body}</div>
      </div>
    </div>
  );
}

export default function LessonPlayerPage() {
  const { id, lessonId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [enrollment, setEnrollment] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/courses/${id}`);
    setCourse(data);
    const l = (data.lessons || []).find((x) => x.id === lessonId) || data.lessons?.[0];
    setLesson(l);
  };
  const loadComments = async () => {
    const { data } = await api.get(`/comments`, { params: { course_id: id, lesson_id: lessonId } });
    setComments(data);
  };
  const loadEnrollment = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/enrollments/me");
      const e = data.find((x) => x.course_id === id);
      setEnrollment(e || null);
    } catch {
      setEnrollment(null);
    }
  };

  useEffect(() => {
    load();
    loadComments();
    loadEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lessonId]);

  // Build a top-level list; attach replies to parents
  const threaded = useMemo(() => {
    const roots = [];
    const repliesByParent = {};
    const sortAsc = (a, b) => new Date(a.created_at) - new Date(b.created_at);
    const sorted = [...comments].sort(sortAsc);
    sorted.forEach((c) => {
      if (c.parent_id) {
        if (!repliesByParent[c.parent_id]) repliesByParent[c.parent_id] = [];
        repliesByParent[c.parent_id].push(c);
      } else {
        roots.push(c);
      }
    });
    return roots.map((r) => ({ ...r, replies: repliesByParent[r.id] || [] }));
  }, [comments]);

  if (!course || !lesson) {
    return <div className="min-h-[40vh] grid place-items-center text-sm text-zinc-500">Loading…</div>;
  }

  const done = enrollment?.progress?.includes(lessonId);

  const markDone = async () => {
    try {
      await api.post("/progress", { course_id: id, lesson_id: lessonId });
      toast.success("Lesson marked complete");
      loadEnrollment();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await api.post("/comments", { course_id: id, lesson_id: lessonId, body: comment });
      setComment("");
      loadComments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submitReply = async (e, parentId) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    try {
      await api.post("/comments", {
        course_id: id,
        lesson_id: lessonId,
        body: replyBody,
        parent_id: parentId,
      });
      setReplyBody("");
      setReplyingTo(null);
      loadComments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment? Replies will also be removed.")) return;
    try {
      await api.delete(`/comments/${commentId}`);
      loadComments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const idx = course.lessons.findIndex((l) => l.id === lesson.id);
  const next = course.lessons[idx + 1];
  const canDelete = (c) => user && (user.id === c.user_id || user.role === "admin");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-12 gap-6">
      <aside className="lg:col-span-3 order-2 lg:order-1">
        <Link to={`/courses/${id}`} className="chip inline-flex items-center gap-1 mb-3" data-testid="back-to-course">
          <ChevronLeft size={14} /> Course
        </Link>
        <div className="eyebrow mb-2">Lessons</div>
        <ol className="space-y-1">
          {course.lessons.map((l, i) => {
            const isCurrent = l.id === lesson.id;
            const isDone = enrollment?.progress?.includes(l.id);
            return (
              <li key={l.id}>
                <button
                  onClick={() => nav(`/learn/${id}/lesson/${l.id}`)}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm border transition ${
                    isCurrent ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--border-warm)] hover:border-[var(--ink)]"
                  }`}
                  data-testid={`sidebar-lesson-${i}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] ${isDone ? "bg-[var(--terracotta)] text-white" : isCurrent ? "bg-white/20 text-white" : "bg-[var(--muted-surface)]"}`}>
                      {isDone ? <Check size={12} /> : i + 1}
                    </span>
                    {l.title}
                  </span>
                </button>
              </li>
            );
          })}
          {course.quiz?.length > 0 && (
            <li>
              <Link to={`/learn/${id}/quiz`} className="block mt-2 chip text-center" data-testid="sidebar-quiz-btn">
                Take final quiz
              </Link>
            </li>
          )}
        </ol>
      </aside>

      <main className="lg:col-span-9 order-1 lg:order-2">
        <div className="eyebrow">{course.title}</div>
        <h1 className="font-display text-3xl sm:text-4xl mb-4" data-testid="lesson-title">
          {lesson.title}
        </h1>

        <div className="aspect-video rounded-3xl overflow-hidden border border-[var(--border-warm)] bg-black">
          {lesson.video_url ? (
            <iframe src={lesson.video_url} className="w-full h-full" title={lesson.title} allow="autoplay; encrypted-media; fullscreen"></iframe>
          ) : (
            <div className="w-full h-full grid place-items-center text-zinc-400">No video yet</div>
          )}
        </div>
        <p className="mt-5 text-zinc-700">{lesson.content}</p>

        <div className="mt-6 flex gap-3">
          {!done ? (
            <button
              className="btn-ink px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"
              onClick={markDone}
              data-testid="mark-complete-btn"
            >
              <Check size={16} /> Mark complete
            </button>
          ) : (
            <span className="chip inline-flex items-center gap-1 text-[var(--sage)] font-bold" data-testid="lesson-done-badge">
              <Check size={14} /> Completed
            </span>
          )}
          {next && (
            <button className="chip" onClick={() => nav(`/learn/${id}/lesson/${next.id}`)} data-testid="next-lesson-btn">
              Next lesson →
            </button>
          )}
        </div>

        <section className="mt-10" data-testid="lesson-discussion">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h2 className="font-display text-2xl inline-flex items-center gap-2">
              <MessageSquare size={20} className="text-[var(--terracotta)]" />
              Discussion
              <span className="text-sm font-sans text-zinc-500">({comments.length})</span>
            </h2>
          </div>

          <form onSubmit={submitComment} className="mb-5" data-testid="comment-form">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={user ? "Ask a question or share a thought with the cohort…" : "Log in to join the discussion"}
              disabled={!user}
              rows={3}
              className="w-full rounded-2xl border border-[var(--border-warm)] bg-white px-4 py-3 text-sm outline-none focus:border-[var(--ink)] disabled:bg-[var(--muted-surface)] resize-y min-h-[72px]"
              data-testid="comment-input"
            />
            <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
              <div className="text-[11px] text-zinc-500">
                {user
                  ? "Be respectful - comments are visible to all enrolled candidates."
                  : "Log in or register to post a comment."}
              </div>
              <button
                type="submit"
                disabled={!user || !comment.trim()}
                className="btn-terracotta px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="comment-submit"
              >
                <Send size={14} /> Post comment
              </button>
            </div>
          </form>

          <div className="space-y-3">
            {threaded.length === 0 && (
              <div
                className="rounded-2xl border border-dashed border-[var(--border-warm)] p-6 text-center text-sm text-zinc-500"
                data-testid="comments-empty"
              >
                No comments yet - be the first to start the conversation.
              </div>
            )}
            {threaded.map((c) => (
              <div
                key={c.id}
                className="rounded-2xl border border-[var(--border-warm)] bg-white p-4"
                data-testid={`comment-${c.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <CommentBody c={c} isReply={false} />
                  {canDelete(c) && (
                    <button
                      onClick={() => deleteComment(c.id)}
                      className="text-zinc-400 hover:text-[var(--terracotta)] transition-colors shrink-0"
                      title="Delete comment"
                      data-testid={`comment-delete-${c.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {user && (
                  <div className="mt-2.5 flex items-center gap-3">
                    <button
                      onClick={() => {
                        setReplyingTo(replyingTo === c.id ? null : c.id);
                        setReplyBody("");
                      }}
                      className="text-xs font-semibold text-[var(--navy)] hover:text-[var(--terracotta)] inline-flex items-center gap-1"
                      data-testid={`comment-reply-btn-${c.id}`}
                    >
                      <Reply size={12} /> {replyingTo === c.id ? "Cancel" : "Reply"}
                    </button>
                    {c.replies.length > 0 && (
                      <span className="text-[11px] text-zinc-500">
                        {c.replies.length} {c.replies.length === 1 ? "reply" : "replies"}
                      </span>
                    )}
                  </div>
                )}

                {replyingTo === c.id && (
                  <form
                    onSubmit={(e) => submitReply(e, c.id)}
                    className="mt-3 flex flex-col sm:flex-row gap-2"
                    data-testid={`reply-form-${c.id}`}
                  >
                    <textarea
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      placeholder={`Reply to ${c.user_name}…`}
                      rows={2}
                      autoFocus
                      className="flex-1 rounded-xl border border-[var(--border-warm)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--ink)] resize-y min-h-[44px]"
                      data-testid={`reply-input-${c.id}`}
                    />
                    <button
                      type="submit"
                      disabled={!replyBody.trim()}
                      className="btn-terracotta px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1 justify-center disabled:opacity-50"
                      data-testid={`reply-submit-${c.id}`}
                    >
                      <Send size={12} /> Reply
                    </button>
                  </form>
                )}

                {c.replies.length > 0 && (
                  <div className="mt-3 pl-4 sm:pl-6 border-l-2 border-[var(--border-warm)] space-y-2">
                    {c.replies.map((r) => (
                      <div
                        key={r.id}
                        className="rounded-xl bg-[var(--muted-surface)]/50 p-3"
                        data-testid={`reply-${r.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <CommentBody c={r} isReply={true} />
                          {canDelete(r) && (
                            <button
                              onClick={() => deleteComment(r.id)}
                              className="text-zinc-400 hover:text-[var(--terracotta)] transition-colors shrink-0"
                              title="Delete reply"
                              data-testid={`reply-delete-${r.id}`}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
